// Required React and utility imports
import { useEffect, useState } from "react";
import { Button, Container, Table } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { BsArrowLeft, BsPencil, BsSearch, BsTrash, BsCopy } from "react-icons/bs";
// UI components and icons


// Timezone handling and formatting
import TimezoneSelect from "react-timezone-select";
import { DateTime } from "luxon";
import { toast } from "sonner";

// Custom utility imports
import { formatDate } from "../../../Utils/helpers/commonhelper";
import { useNavigate } from "react-router-dom";
import { DescriptionTooltip } from "../../../Utils/helpers/descriptionTooltip/DescriptionTooltip";
import { TableSkeleton } from "../../../Utils/skeletonLoader/TableSkeleton";
import "./CreateShift.css";
import nodata from "../../../Utils/assets/img/leads_no_data_found.png";
// API handlers
import {
  DeleteRequest,
  GetRequest,
  PutRequest,
} from "../../../Utils/hooks/ApiHandler";
import DeleteModal from "../../DeleteModal";

import useLocalStorageState from "use-local-storage-state";
import {
  useGetRequest,
  usePostRequest,
} from "../../../Utils/hooks/QueryHandler";
import { IconPlus } from "@tabler/icons-react";

// Weekday array used for shift timing fields
const days = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

// Dynamic time field creation using Zod schema
const timeFields = days.reduce((acc, day) => {
  acc[`${day}_from`] = z.string().optional();
  acc[`${day}_to`] = z.string().optional();
  return acc;
}, {});

// Final shift form validation schema
const shiftSchema = z.object({
  name: z.string().min(1, "Shift Name is required"),
  timezone: z.object({
    value: z.string(),
    label: z.string(),
  }),
  description: z.string().optional(),
  ...timeFields,
});

// Main component
export const CreateShift = () => {
  // Session storage for customer token
  const [customer, setCustomer] = useLocalStorageState("customer", {
    token: {},
  });
  // React Router navigation hook
  const navigate = useNavigate();

  // State variables for shifts and form
  const [shifts, setShifts] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [editingShift, setEditingShift] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [shiftToDelete, setShiftToDelete] = useState(null);
  // delete modal loading state
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState("");
  // Active day tracking (for shift schedule)
  const [activeDays, setActiveDays] = useState(
    Object.fromEntries(days.map((d) => [d, true]))
  );
  const customerId = customer?.customer_details?.customer_id;
  // Default time zone


  // master permissions 
  const permission = customer.customer_details?.permission;
  const user_type = Number(customer.customer_details?.user_type)
  const settingsPermission = permission?.filter(c => c.module_name === "setting")
  // view permission for master 
  const masterGrant = {
    isView: user_type !== 1 ? Boolean(Number(settingsPermission?.find(c => c.feature_name === "master")?.view)) : true,
    isCreate: user_type !== 1 ? Boolean(Number(settingsPermission?.find(c => c.feature_name === "master")?.create)) : true,
    isEdit: user_type !== 1 ? Boolean(Number(settingsPermission?.find(c => c.feature_name === "master")?.edit)) : true,
    isDelete: user_type !== 1 ? Boolean(Number(settingsPermission?.find(c => c.feature_name === "master")?.delete)) : true,
  }
  const defaultTimezone = {
    value: "Asia/Kolkata",
    label: "Indian Standard Time",
  };

  // Default time values for each day
  const defaultTimeValues = Object.fromEntries(
    days.flatMap((day) => [
      [`${day}_from`, "09:00"],
      [`${day}_to`, "18:00"],
    ])
  );

  // React Hook Form setup with Zod resolver
  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(shiftSchema),
    defaultValues: {
      name: "",
      timezone: defaultTimezone,
      description: "",
      ...defaultTimeValues,
    },
  });

  // Auth tokens and headers

  const token = customer?.token?.authorization;
  const rf = customer?.token?.authentication;
  const extras = { customer, setCustomer, rf, navigate };

  const headers = {
    authentication: rf,
    Authorization: token,
  };

  // Fetch all shifts for the current customer

  const {
    data: shiftData,
    error,
    refetch: fetchAllShift,
    isLoading,

    isError,
  } = useGetRequest(
    `/masters/getShifts/${customerId}`,
    "getShift",

    headers,

    extras
  );
  // Fetch departments on component mount
  useEffect(() => {
    if (shiftData?.status === "success") {
      setShifts(shiftData.data);
    } else if (isError) {
      console.error("Error fetching roles:", error);
    }
  }, [shiftData, isError]);
  // Form submit handler (Create / Update Shift)
  const { mutate: createShift } = usePostRequest(
    `masters/createShift`,
    null,
    { headers },
    extras
  );

  const onSubmit = async (formData) => {
    const name = formData.name.trim();
    const description = formData.description?.trim() || "";
    const timezone = formData.timezone === "null" ? null : formData.timezone;

    const isDuplicate = shifts.some(
      (d) => d.shift_name.trim() === name && d.id !== editingShift?.id
    );

    if (isDuplicate) {
      toast.error(`Duplicate shift_name ${name}`);
      return;
    }

    const shift_timings = days
      .filter((day) => activeDays[day])
      .map((day) => ({
        day,
        from: formData[`${day}_from`],
        to: formData[`${day}_to`],
      }));

    const basePayload = {
      customer_id: customerId,
      shift_name: name,
      description,
      timezone,
      shift_timings,
    };

    const payload = editMode
      ? { ...basePayload, shift_id: editingShift?.id }
      : { ...basePayload, login_user_id: customer.customer_details.id };

    if (editMode) {
      //UPDATE MODE
      try {
        const res = await PutRequest(
          `/masters/updateShift`,
          payload,
          { headers },
          extras
        );

        if (res?.status === "success") {
          toast.success("Shift updated successfully!");
          await fetchAllShift();
          setEditMode(false);
          setEditingShift(null);
          setActiveDays(Object.fromEntries(days.map((d) => [d, true])));
          reset({
            name: "",
            timezone: null,
            description: "",
            ...Object.fromEntries(
              days.flatMap((d) => [
                [`${d}_from`, "09:00"],
                [`${d}_to`, "18:00"],
              ])
            ),
          });
          setShowForm(false);
        } else {
          toast.error(res?.message || "Failed to update shift");
        }
      } catch (err) {
        console.error("Update shift error:", err);
        toast.error("Shift update failed");
      }
    } else {
      // CREATE MODE (mutation)
      createShift(payload, {
        onSuccess: (res) => {
          if (res?.status === "success") {
            toast.success("Shift added successfully!");
            fetchAllShift();
            reset();
            setShowForm(false);
            setEditMode(false);
            setEditingShift(null);
            setActiveDays(Object.fromEntries(days.map((d) => [d, true])));
          } else {
            toast.error(res.message || "Failed to add shift");
          }
        },
        onError: (err) => {
          toast.error(err?.message || "Add shift request failed");
        },
      });
    }
  };

  // Edit existing shift handler

  const onEdit = async (shift) => {
    try {
      setShowForm(true);
      setEditMode(true);
      setEditingShift(shift);

      const res = await GetRequest(
        `/masters/getShiftById/${shift.id}`,
        { headers },
        extras
      );

      if (res.status === "success") {
        const shiftData = res.data[0];

        // Parse timing and populate form
        const parsedTimings = JSON.parse(shiftData.shift_timings || "[]");
        const active = {};
        const timeValues = {};

        days.forEach((day) => {
          const match = parsedTimings.find((d) => d.day === day);
          if (match) {
            active[day] = true;
            timeValues[`${day}_from`] = match.from;
            timeValues[`${day}_to`] = match.to;
          } else {
            active[day] = false;
            timeValues[`${day}_from`] = "09:00";
            timeValues[`${day}_to`] = "18:00";
          }
        });

        setActiveDays(active);
        reset({
          name: shiftData.shift_name,
          timezone: JSON.parse(shiftData.timezone),
          description: shiftData.description,
          ...timeValues,
        });
      }
    } catch (err) {
      console.error("Error in onEdit:", err);
      toast.error("Failed to load shift details.");
    }
  };
  // Clone existing shift handler
  const onClone = async (shift) => {
    try {
      setShowForm(true);
      setEditingShift(shift);
      const res = await GetRequest(
        `/masters/getShiftById/${shift.id}`,
        { headers },
        extras
      );

      if (res.status === "success") {
        const shiftData = res.data[0];

        const parsedTimings = JSON.parse(shiftData.shift_timings || "[]");
        const active = {};
        const timeValues = {};

        days.forEach((day) => {
          const match = parsedTimings.find((d) => d.day === day);
          if (match) {
            active[day] = true;
            timeValues[`${day}_from`] = match.from;
            timeValues[`${day}_to`] = match.to;
          } else {
            active[day] = false;
            timeValues[`${day}_from`] = "09:00";
            timeValues[`${day}_to`] = "18:00";
          }
        });

        setActiveDays(active);
        reset({
          name: shiftData.shift_name + "clone",
          timezone: JSON.parse(shiftData.timezone),
          description: shiftData.description,
          ...timeValues,
        });
      }
    } catch (err) {
      console.error("Error in onEdit:", err);
      toast.error("Failed to load shift details.");
    }
  };

  // Trigger delete modal with selected shift
  const handleDeleteClick = (shift) => {
    setShiftToDelete(shift);
    setShowDeleteModal(true);
  };
  // Confirm and execute delete action
  const confirmDelete = async () => {
    if (!shiftToDelete) return;
    setLoading(true)
    try {
      const res = await DeleteRequest(
        `masters/deleteShift/${shiftToDelete.id}`,
        { headers },
        extras
      );

      if (res.status === "success") {
        const updatedShifts = shifts.filter(
          (shift) => shift.id !== shiftToDelete.id
        );
        setShifts(updatedShifts);
        await fetchAllShift();
        setLoading(false)
        toast.success(res.message);

        setShowDeleteModal(false);
        setShiftToDelete(null);
      } else {
        setLoading(false)
        toast.error(res.message);
      }
    } catch (err) {
      setLoading(false)
      console.error("Delete shift error:", err);
      toast.error("Failed to delete shift.");
    }
  };
  // Find first checked day to copy timings
  const firstCheckedDay = days.find((d) => activeDays[d]);

  const copyToAll = () => {
    if (!firstCheckedDay) return;
    const from = watch(`${firstCheckedDay}_from`);
    const to = watch(`${firstCheckedDay}_to`);
    days.forEach((day) => {
      if (day !== firstCheckedDay && activeDays[day]) {
        setValue(`${day}_from`, from);
        setValue(`${day}_to`, to);
      }
    });
  };

  return (
    <div className="container-fluid p-3 create-shift-container mb-3 ">
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
        {/* <div>
          <h2 className="mb-2 mb-md-0" style={{ fontSize: "1.5rem" }}>
            {showForm ? "Create Shift" : "List of Shifts"}
          </h2>
          <p>{!showForm && "List of current shifts within your organization."}</p>
          </div> */}
        <div >
          <h5 className="fw-semibold mb-1">
            {showForm ? "Create Shift" : "List of Shifts"}
          </h5>
          <p className="text-muted small">
            {!showForm && "List of current shifts within your organization."}
          </p>
        </div>

        <div className="d-flex align-items-center gap-2">
          {/* Back Button */}
          {!showForm && masterGrant?.isView && (
            <div className="d-flex align-items-center gap-2 ms-3">
              <div className="position-relative">
                <input
                  type="search"
                  className="form-control"
                  placeholder="Search shifts..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ fontSize: "12px", paddingLeft: "36px", height: "38px", width: "220px" }}
                />
                <i
                  className="fa-solid fa-magnifying-glass"
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "12px",
                    transform: "translateY(-50%)",
                    fontSize: "12px",
                  }}
                ></i>
              </div>

              <button
                className="btn btn-default d-flex align-items-center"
                onClick={() => {
                  if (!masterGrant?.isCreate) {
                    toast.warning("You are not authorized for this action!");
                    return;
                  }
                  reset();
                  setActiveDays(Object.fromEntries(days.map((d) => [d, true])));
                  setShowForm(true);
                  setEditMode(false);
                  setEditingShift(null);
                }}
                style={{
                  height: "37px",
                  color: "white",
                  fontSize: "14px",
                  border: "1px solid",
                  paddingRight: "10px",
                  backgroundColor: "rgb(122, 58, 94)",
                }}
              >
                Add Shift
                <IconPlus style={{ width: '18px', height: '16px', paddingLeft: "4px" }}></IconPlus>
              </button>
            </div>
          )}
          <div
            className="btn btn-default-outline"
            onClick={() => {
              if (showForm && !editMode && !editingShift) {
                setShowForm(false);
                reset();
                setActiveDays(Object.fromEntries(days.map((d) => [d, true])));
              } else {
                navigate(-1);
              }
            }}
          >
            <BsArrowLeft className="me-1" /> Back
          </div>
        </div>
      </div>
      <hr
        className="mb-4"
        style={{ borderTop: "0.5px solid #6c757d", opacity: "0.3", marginTop: "0.7rem" }}
      />
      {showForm && (
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="border px-4 py-3 bg-white rounded mb-0"
        >
          <div className="row mb-3 ">
            <div className="col-md-6">
              <label>
                Shift Name<span className="text-danger">*</span>
              </label>
              <input {...register("name")} className="form-control" />
              {errors.name && (
                <small className="text-danger">{errors.name.message}</small>
              )}
            </div>
            <div className="col-md-6">
              <label>Timezone</label>
              <TimezoneSelect
                value={watch("timezone") || defaultTimezone}
                onChange={(val) => {
                  const oldZone = watch("timezone")?.value || "Asia/Kolkata";
                  const newZone = val.value;

                  days.forEach((day) => {
                    if (activeDays[day]) {
                      const fromVal = getValues(`${day}_from`);
                      const toVal = getValues(`${day}_to`);

                      const fromDateTime = DateTime.fromFormat(
                        fromVal,
                        "HH:mm",
                        {
                          zone: oldZone,
                        }
                      );
                      const toDateTime = DateTime.fromFormat(toVal, "HH:mm", {
                        zone: oldZone,
                      });

                      const newFrom = fromDateTime
                        .setZone(newZone)
                        .toFormat("HH:mm");
                      const newTo = toDateTime
                        .setZone(newZone)
                        .toFormat("HH:mm");

                      setValue(`${day}_from`, newFrom);
                      setValue(`${day}_to`, newTo);
                    }
                  });

                  setValue("timezone", val);
                }}
              />

              {errors.timezone && (
                <small className="text-danger">{errors.timezone.message}</small>
              )}
            </div>

          </div>

          <div className="row mb-4 ">
            <div className="col-md-12">
              <label>Description</label>
              <textarea {...register("description")} className="form-control" />
              {errors.description && (
                <small className="text-danger">
                  {errors.description.message}
                </small>
              )}
            </div>
          </div>

          <div
            style={{
              maxHeight: "300px",
              overflowY: "auto",
              border: "1px solid #dee2e6",
              borderRadius: "0.25rem",
            }}
          >
            <table className="table mb-0 table-bordered">
              <thead className="bg-primary text-white">
                <tr>
                  <th>Day</th>
                  <th>From</th>
                  <th>To</th>
                  <th>Copy</th>
                </tr>
              </thead>
              <tbody>
                {days.map((day) => (
                  <tr key={day}>
                    <td>
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input me-2"
                          checked={activeDays[day]}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setActiveDays((prev) => ({
                              ...prev,
                              [day]: checked,
                            }));
                            if (checked) {
                              setValue(
                                `${day}_from`,
                                getValues(`${day}_from`) || "09:00"
                              );
                              setValue(
                                `${day}_to`,
                                getValues(`${day}_to`) || "18:00"
                              );
                            } else {
                              setValue(`${day}_from`, undefined);
                              setValue(`${day}_to`, undefined);
                            }
                          }}
                          id={`check-${day}`}
                        />
                        <label
                          htmlFor={`check-${day}`}
                          className="form-check-label text-capitalize fw-semibold"
                        >
                          {day}
                        </label>
                      </div>
                    </td>
                    {activeDays[day] ? (
                      <>
                        <td>
                          <input
                            type="time"
                            className="form-control"
                            {...register(`${day}_from`)}
                          />
                        </td>
                        <td>
                          <input
                            type="time"
                            className="form-control"
                            {...register(`${day}_to`)}
                          />
                        </td>
                      </>
                    ) : (
                      <td
                        colSpan={2}
                        className="text-muted fw-bold text-center"
                      >
                        **Weekly Off**
                      </td>
                    )}
                    <td>
                      {day === firstCheckedDay && activeDays[day] && (
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={copyToAll}
                        >
                          Copy To All
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="d-flex justify-content-end mt-4 gap-2 mb-3">
            <button
              className="me-2 btn btn-default-outline btn btn-custom-variant"
              type="button"
              onClick={() => {
                setShowForm(false);
                setEditMode(false);
                setEditingShift(null);
              }}
            >
              Cancel
            </button>
            <button className="btn btn-default" type="submit">
              {editMode ? "Update Shift" : "Save Shift"}
            </button>

          </div>
        </form>
      )}
      {!showForm && (
        <div
          className=" "
          style={{
            // border: "1px solid #dee2e6",
            borderRadius: "0.25rem",
            overflow: "hidden",
          }}
        >
          <div>
            {/* Fixed Header */}
            {
              masterGrant?.isView ?
                <Container
                  fluid
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    height: "calc(100vh - 10rem)",
                    backgroundColor: "white",
                    padding: 0,
                    animation: "cardEntry 1s ease-out forwards",
                  }}
                  className="mt-0 p-0"
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                      width: "100%",
                    }}
                  >

                    <div
                      className="listStyleForContact container-table"
                      style={{
                        flex: shifts?.length > 10 ? 1 : "auto",
                        overflow: "auto",
                        width: "100%",
                        maxHeight:
                          shifts?.length > 10 ? "100%" : "auto",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.scrollbarColor = "#e0e0e0 white"

                      }}
                    >
                      <Table >
                        <colgroup>
                          <col style={{ width: "7%" }} />
                          <col style={{ width: "18%" }} />
                          <col style={{ width: "25%" }} />
                          <col style={{ width: "20%" }} />
                          <col style={{ width: "15%" }} />
                          <col style={{ width: "15%" }} />
                        </colgroup>
                        <thead style={{ zIndex: "10" }}>
                          <tr>
                            <th>S NO.</th>
                            <th>Name</th>
                            <th>Description</th>
                            <th>Created At</th>
                            <th>Created By</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {isLoading ? <TableSkeleton columnCount={6} /> :
                            <>
                              {shifts
                                .filter((d) =>
                                  d.shift_name
                                    .toLowerCase()
                                    .includes(searchTerm.trim().toLowerCase())
                                )
                                .map((shift, index) => (
                                  <tr key={index}>
                                    <td>{index + 1}</td>
                                    <td style={{ verticalAlign: "middle" }}>
                                      {shift.shift_name}
                                    </td>
                                    <td style={{ verticalAlign: "middle" }}>
                                      <DescriptionTooltip
                                        text={shift.description || "-"}
                                        maxLength={50}
                                      />
                                    </td>
                                    <td style={{ verticalAlign: "middle" }}>
                                      {formatDate(shift.created_at)}
                                    </td>
                                    <td style={{ verticalAlign: "middle" }}>
                                      {shift.userName}
                                    </td>
                                    <td>
                                      <div className="d-flex gap-2">
                                        <Button
                                          size="sm"
                                          variant="outline-primary"
                                          onClick={() => {
                                            if (!masterGrant?.isEdit) {
                                              toast.warning("You are not authorized for this action!")
                                              return
                                            }
                                            onEdit(shift)
                                          }}
                                          title="Edit"
                                        >
                                          <BsPencil />
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="outline-danger"
                                          title="Delete"
                                          onClick={() => {
                                            if (!masterGrant?.isDelete) {
                                              toast.warning("You are not authorized for this action!")
                                              return
                                            }
                                            handleDeleteClick(shift)
                                          }
                                          }
                                        >
                                          <BsTrash />
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="outline-secondary"
                                          onClick={() => {
                                            if (!masterGrant?.isCreate) {
                                              toast.warning("You are not authorized for this action!")
                                              return
                                            }
                                            onClone(shift)
                                          }
                                          }
                                          title="Clone"
                                        >
                                          <BsCopy />
                                        </Button>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              {shifts.filter((d) =>
                                d.shift_name
                                  .toLowerCase()
                                  .includes(searchTerm.trim().toLowerCase())
                              ).length === 0 && (
                                  <td
                                    colSpan="14"
                                    style={{ textAlign: "center", color: "gray" }}
                                  >
                                    <div
                                      class="d-flex justify-content-center align-items-center"
                                      style={{ position: "absolute", left: "40%" }}
                                    >
                                      <img
                                        src={nodata}
                                        alt=""
                                        style={{ width: "17rem", marginTop: "0" }}
                                      />
                                    </div>
                                  </td>
                                )}
                            </>
                          }
                        </tbody>
                      </Table>

                      {/* Scrollable Body */}

                    </div>
                  </div>
                </Container>
                :
                <>You are not authorized to view Shift's listing!</>
            }

          </div>
        </div>
      )}

      <DeleteModal
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        isLoading={loading}
        message="Are you sure you want to delete this item?"
      />
    </div>
  );
};